import { useState, useEffect, useCallback } from 'react';

/**
 * Lists available cameras/microphones/speakers via enumerateDevices().
 * Device labels stay empty until the caller has obtained getUserMedia
 * permission at least once — this hook does not request permission itself,
 * call refresh() again after your own getUserMedia call resolves.
 */
export function useMediaDevices() {
  const [cameras, setCameras] = useState([]);
  const [microphones, setMicrophones] = useState([]);
  const [speakers, setSpeakers] = useState([]);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter(d => d.kind === 'videoinput'));
      setMicrophones(devices.filter(d => d.kind === 'audioinput'));
      setSpeakers(devices.filter(d => d.kind === 'audiooutput'));
    } catch (e) {
      console.warn('[useMediaDevices] enumerateDevices failed:', e.message);
    }
  }, []);

  useEffect(() => {
    refresh();
    if (!navigator.mediaDevices?.addEventListener) return;
    navigator.mediaDevices.addEventListener('devicechange', refresh);
    return () => navigator.mediaDevices.removeEventListener('devicechange', refresh);
  }, [refresh]);

  return { cameras, microphones, speakers, refresh };
}
